import * as React from "react"
import { Combobox as ComboboxPrimitive } from "@base-ui/react"

import { Button } from "@/primitives/button"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/primitives/input-group"
import { cn } from "@/primitives/utils"
import { ChevronsUpDownIcon, XIcon, CheckIcon, SearchIcon } from "lucide-react"

const Combobox = ComboboxPrimitive.Root

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-3.5", className)}
      {...props}
    >
      {children}
      {/* beacon: the same up-down chevron as Select, in the tertiary tier. */}
      <ChevronsUpDownIcon className="pointer-events-none size-3.5 text-subtle-foreground" />
    </ComboboxPrimitive.Trigger>
  )
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      render={<InputGroupButton variant="ghost" size="icon-xs" />}
      className={cn(className)}
      {...props}
    >
      <XIcon className="pointer-events-none" />
    </ComboboxPrimitive.Clear>
  )
}

/*
 * beacon: two shapes. `field` is a form control that opens the list (with its chevron and an
 * optional clear). `search` is the bare search row at the top of a popup or picker: a search icon,
 * no chrome but a hairline beneath it, 36px tall.
 */
function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  variant = "field",
  ...props
}: ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean
  showClear?: boolean
  variant?: "field" | "search"
}) {
  if (variant === "search") {
    return (
      <InputGroup
        data-variant="search"
        className={cn(
          // beacon: the bare search row.
          "h-9 w-full rounded-none border-0 border-b border-border-subtle bg-transparent px-1 shadow-none hover:border-border-subtle has-[[data-slot=input-group-control]:focus-visible]:border-border-subtle has-[[data-slot=input-group-control]:focus-visible]:ring-0 dark:bg-transparent",
          className,
        )}
      >
        <InputGroupAddon align="inline-start">
          <SearchIcon />
        </InputGroupAddon>
        <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} />} {...props} />
        {children}
      </InputGroup>
    )
  }

  return (
    <InputGroup className={cn("w-auto", className)}>
      <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} />} {...props} />
      <InputGroupAddon align="inline-end">
        {showTrigger && (
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            render={<ComboboxTrigger />}
            data-slot="input-group-button"
            className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
            disabled={disabled}
          />
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  )
}

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset" | "anchor"
  >) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            // beacon: the shared popup (as Select's): at least the anchor's width, otherwise its
            // content's up to 28rem, lifted by the popover shadow. The list inside carries the
            // padding, so a search row runs edge to edge.
            "group/combobox-content relative flex max-h-(--available-height) w-max max-w-[min(var(--container-md),var(--available-width))] min-w-(--anchor-width) origin-(--transform-origin) flex-col overflow-hidden rounded-lg bg-popover text-ui text-popover-foreground shadow-popover duration-150 ease-snappy data-[side=bottom]:slide-in-from-top-1 data-[side=inline-end]:slide-in-from-left-1 data-[side=inline-start]:slide-in-from-right-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  )
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      // beacon: up to 384px of rows before it scrolls.
      className={cn(
        "no-scrollbar max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0",
        className,
      )}
      {...props}
    />
  )
}

/*
 * beacon: rows as Select's: 32px, the popover's accent on highlight. `indicator` puts the chosen mark in a 16px
 * slot on the left: a check for a single choice, or a checkbox for a multiple one.
 */
function ComboboxItem({
  className,
  children,
  indicator = "check",
  ...props
}: ComboboxPrimitive.Item.Props & { indicator?: "check" | "checkbox" }) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      data-indicator={indicator}
      className={cn(
        "group/combobox-item relative flex h-8 w-full cursor-default items-center gap-2 rounded-md pr-2 pl-7 text-ui outline-hidden select-none data-highlighted:bg-popover-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {indicator === "checkbox" ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center rounded-sm border border-subtle-foreground bg-background text-primary-foreground group-data-[selected]/combobox-item:border-primary group-data-[selected]/combobox-item:bg-primary"
        >
          <ComboboxPrimitive.ItemIndicator render={<CheckIcon className="size-3" />} />
        </span>
      ) : (
        <ComboboxPrimitive.ItemIndicator
          render={
            <span className="pointer-events-none absolute left-2 flex size-4 items-center justify-center" />
          }
        >
          <CheckIcon className="pointer-events-none size-3.5" />
        </ComboboxPrimitive.ItemIndicator>
      )}
      {children}
    </ComboboxPrimitive.Item>
  )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      // beacon: a group's label is tertiary meta text, as in Select.
      className={cn("px-2 py-1.5 text-xs font-medium text-subtle-foreground", className)}
      {...props}
    />
  )
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
}

function ComboboxEmpty({
  className,
  children = "No matches",
  ...props
}: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        // beacon: a quiet sentence where the rows would be; "No matches" unless told otherwise.
        "hidden w-full justify-center py-6 text-center text-ui text-subtle-foreground group-data-empty/combobox-content:flex",
        className,
      )}
      {...props}
    >
      {children}
    </ComboboxPrimitive.Empty>
  )
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      // beacon: the subtle hairline.
      className={cn("-mx-1 my-1 h-px bg-border-subtle", className)}
      {...props}
    />
  )
}

/** beacon: a footer that stays put under the list, for a picker's confirming action. */
function ComboboxFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="combobox-footer"
      className={cn(
        "sticky bottom-0 flex items-center gap-2 border-t border-border-subtle bg-popover p-1.5",
        className,
      )}
      {...props}
    />
  )
}

function ComboboxChips({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        // beacon: the Input's metrics and states.
        "flex min-h-8 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-ui transition-[border-color,box-shadow] duration-100 ease-snappy hover:border-border-strong focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/20 has-aria-invalid:border-destructive has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/15 has-data-[slot=combobox-chip]:px-1 dark:bg-surface-sunken",
        className,
      )}
      {...props}
    />
  )
}

function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean
}) {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        // beacon: a neutral 20px chip, like a Badge.
        "flex h-5 w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0",
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant="ghost" size="icon-xs" />}
          className="-ml-1 size-5 opacity-60 hover:bg-transparent hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <XIcon className="pointer-events-none size-3" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  )
}

function ComboboxChipsInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      // beacon: a subtle placeholder, as the Input's.
      className={cn(
        "min-w-16 flex-1 bg-transparent outline-none placeholder:text-subtle-foreground",
        className,
      )}
      {...props}
    />
  )
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null)
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxSeparator,
  ComboboxFooter,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
}
